/**
 * Transforms for Dental PPO
 *
 * Maps between the flat Grid format and the nested API format.
 * - Grid: Flat fields with 'Out' suffix for out-of-network
 * - API: networkBenefits array with separate In/Out objects
 */

import {
  DentalObjectTypeEnum,
  RfpOrthoAgeLimitType,
} from "@platform/openapi-types";

import { RfpDentalBenefit, RfpDentalNetworkBenefit } from "../v5-types";
import {
  shouldIncludeBenefitInApi,
  transformTypeAmountToApi,
} from "../shared/builders";

import type {
  DentalExtendedBenefitApi,
  DentalPpoBenefitGrid,
  DentalPpoNetworkBenefit,
} from "./types";

/**
 * Builds a network benefit object for the API from a single network's grid data
 */
const buildNetworkBenefitToApi = (
  networkBenefit: DentalPpoNetworkBenefit,
): RfpDentalNetworkBenefit => ({
  network: networkBenefit.network,

  ...(shouldIncludeBenefitInApi(networkBenefit.networkName)
    ? {
        networkName: networkBenefit.networkName,
      }
    : {}),

  ...(shouldIncludeBenefitInApi(networkBenefit.sealants)
    ? {
        sealants: networkBenefit.sealants,
      }
    : {}),
  ...(shouldIncludeBenefitInApi(networkBenefit.spaceMaintainers)
    ? {
        spaceMaintainers: networkBenefit.spaceMaintainers,
      }
    : {}),
  ...(shouldIncludeBenefitInApi(networkBenefit.generalAnesthesia)
    ? {
        generalAnesthesia: networkBenefit.generalAnesthesia,
      }
    : {}),
  ...(shouldIncludeBenefitInApi(networkBenefit.compositeWhiteFillings)
    ? {
        compositeWhiteFillings: networkBenefit.compositeWhiteFillings,
      }
    : {}),
  ...(shouldIncludeBenefitInApi(networkBenefit.endodontics)
    ? {
        endodontics: networkBenefit.endodontics,
      }
    : {}),
  ...(shouldIncludeBenefitInApi(networkBenefit.periodonticsNonSurgical)
    ? {
        periodonticsNonSurgical: networkBenefit.periodonticsNonSurgical,
      }
    : {}),
  ...(shouldIncludeBenefitInApi(networkBenefit.periodonticsSurgical)
    ? {
        periodonticsSurgical: networkBenefit.periodonticsSurgical,
      }
    : {}),
  ...(shouldIncludeBenefitInApi(networkBenefit.oralSurgerySimple)
    ? {
        oralSurgerySimple: networkBenefit.oralSurgerySimple,
      }
    : {}),
  ...(shouldIncludeBenefitInApi(networkBenefit.oralSurgeryComplex)
    ? {
        oralSurgeryComplex: networkBenefit.oralSurgeryComplex,
      }
    : {}),
  ...(shouldIncludeBenefitInApi(networkBenefit.implants)
    ? {
        implants: networkBenefit.implants,
      }
    : {}),

  ...(shouldIncludeBenefitInApi(networkBenefit.individualDeductibleType)
    ? {
        individualDeductible: transformTypeAmountToApi(
          networkBenefit.individualDeductibleType,
          networkBenefit.individualDeductibleAmount,
          ["dollarAmount"],
        ) as RfpDentalNetworkBenefit["individualDeductible"],
      }
    : {}),

  familyDeductible: transformTypeAmountToApi(
    networkBenefit.familyDeductibleType,
    networkBenefit.familyDeductibleAmount,
    ["dollarAmount", "dollarAmountPerPerson"],
  ) as RfpDentalNetworkBenefit["familyDeductible"],

  annualMaximum: transformTypeAmountToApi(
    networkBenefit.annualMaximumType,
    networkBenefit.annualMaximumAmount,
    ["dollarAmount", "dollarAmountPerPerson"],
  ) as RfpDentalNetworkBenefit["annualMaximum"],

  preventativeType1: transformTypeAmountToApi(
    networkBenefit.preventativeType1Type,
    networkBenefit.preventativeType1Amount,
    ["percentageAmount"],
  ) as RfpDentalNetworkBenefit["preventativeType1"],

  basicType2: transformTypeAmountToApi(
    networkBenefit.basicType2Type,
    networkBenefit.basicType2Amount,
    ["percentageAmount"],
  ) as RfpDentalNetworkBenefit["basicType2"],

  majorType3: transformTypeAmountToApi(
    networkBenefit.majorType3Type,
    networkBenefit.majorType3Amount,
    ["percentageAmount"],
  ) as RfpDentalNetworkBenefit["majorType3"],

  orthoType4: transformTypeAmountToApi(
    networkBenefit.orthoType4Type,
    networkBenefit.orthoType4Amount,
    ["percentageAmount"],
  ) as RfpDentalNetworkBenefit["orthoType4"],

  orthoMaximum: transformTypeAmountToApi(
    networkBenefit.orthoMaximumType,
    networkBenefit.orthoMaximumAmount,
    ["dollarAmount", "dollarAmountPerPerson"],
  ) as RfpDentalNetworkBenefit["orthoMaximum"],

  orthoAgeLimit: transformTypeAmountToApi(
    networkBenefit.orthoAgeLimitType,
    networkBenefit.orthoAgeLimitAge,
    [RfpOrthoAgeLimitType.childrenOnlyToAge],
    "age",
  ) as RfpDentalNetworkBenefit["orthoAgeLimit"],
});

/**
 * Transform from Grid format to API format
 */
export const transformDentalPpoBenefitsToApi = (
  benefits: DentalPpoBenefitGrid,
): Partial<RfpDentalBenefit> & { extended?: DentalExtendedBenefitApi } => {
  const transformed: Partial<RfpDentalBenefit> & {
    extended?: DentalExtendedBenefitApi;
  } = {
    objectType: DentalObjectTypeEnum.dental,
    ...(shouldIncludeBenefitInApi(benefits.deductibleDuration)
      ? {
          deductibleDuration: benefits.deductibleDuration,
        }
      : {}),
    ...(shouldIncludeBenefitInApi(benefits.isDeductibleWaivedPreventative)
      ? {
          isDeductibleWaivedPreventative:
            benefits.isDeductibleWaivedPreventative,
        }
      : {}),
    ...(shouldIncludeBenefitInApi(benefits.outOfNetworkReimbursement)
      ? {
          outOfNetworkReimbursement: benefits.outOfNetworkReimbursement,
        }
      : {}),
    ...(shouldIncludeBenefitInApi(benefits.maximumRollover)
      ? {
          maximumRollover: benefits.maximumRollover,
        }
      : {}),
    ...(shouldIncludeBenefitInApi(benefits.missingToothExclusion)
      ? {
          missingToothExclusion: benefits.missingToothExclusion,
        }
      : {}),
    ...(shouldIncludeBenefitInApi(benefits.annualOpenEnrollment)
      ? {
          annualOpenEnrollment: benefits.annualOpenEnrollment,
        }
      : {}),
    ...(shouldIncludeBenefitInApi(benefits.dependentAgeLimitYears)
      ? {
          dependentAgeLimitYears: benefits.dependentAgeLimitYears,
        }
      : {}),
    ...(shouldIncludeBenefitInApi(benefits.employerContribution)
      ? {
          employerContribution: benefits.employerContribution,
        }
      : {}),
  };

  if (Array.isArray(benefits.serviceWaitingPeriods)) {
    transformed.serviceWaitingPeriods = benefits.serviceWaitingPeriods;
  } else if (Array.isArray(benefits.serviceWaitingPeriodsMonths)) {
    transformed.serviceWaitingPeriods = benefits.serviceWaitingPeriodsMonths;
  }

  // networkName is per-network — buildNetworkBenefitToApi carries each network's
  // own value, so the network benefits map straight through.
  if (benefits.networkBenefits?.length) {
    transformed.networkBenefits = benefits.networkBenefits.map(
      buildNetworkBenefitToApi,
    );
  }

  // Extended (non-v5) fields — CD-1659/CD-1660. Sent as a full object (a
  // full-replace PATCH, like the rest of `benefits`) whenever at least one
  // extended field is set, so an edit to any other field on the plan
  // doesn't drop the extended values already on it. Omitted entirely when
  // every extended field is unset, so an edit that never touches these
  // fields doesn't create an empty extended row on the backend.
  const extended = buildDentalPpoExtendedForApi(benefits);
  if (
    Object.values(extended).some((value) => value !== undefined && value !== "")
  ) {
    transformed.extended = extended;
  }

  return transformed;
};

// Extended (non-v5) fields — CD-1659/CD-1660. Split out of
// transformDentalPpoBenefitsToApi to keep that function's complexity down.
const buildDentalPpoExtendedForApi = (
  benefits: DentalPpoBenefitGrid,
): DentalExtendedBenefitApi => ({
  preventativeAdvantage: benefits.preventiveAdvantage,
  oralExams: benefits.oralExams,
  oralExamFrequency: benefits.oralExamFrequency,
  cleaningFrequency: benefits.cleaningFrequency,
  bitewingXRays: benefits.bitewingXRays,
  fullMouthXRays: benefits.fullMouthXRays,
  fullMouthXRaysFrequency: benefits.fullMouthXRaysFrequency,
  fluorideTreatments: benefits.fluorideTreatments,
  amalgamSilverFillings: benefits.amalgamSilverFillings,
  simpleExtractions: benefits.simpleExtractions,
  bridgesAndDentures: benefits.bridgesDentures,
  bridgesAndDenturesFrequency: benefits.bridgesDenturesFrequency,
  crowns: benefits.crowns,
  crownsFrequency: benefits.crownsFrequency,
  tmj: benefits.tmj,
  workInProgressIncluded: benefits.workInProgressIncluded,
  openOrClosedContract: benefits.openOrClosedContract,
  eligibilityHours: benefits.eligibilityHours,
  weightedAvgOutOfPocket: benefits.weightedAvgOutOfPocket,
});

/**
 * Helper to extract network fields from API object to Grid format
 */
const transformNetworkBenefitFromApi = (
  networkBenefit: RfpDentalNetworkBenefit,
): DentalPpoNetworkBenefit => ({
  ...networkBenefit,
  individualDeductibleType: networkBenefit.individualDeductible?.type,
  individualDeductibleAmount: networkBenefit.individualDeductible?.amount,
  familyDeductibleType: networkBenefit.familyDeductible?.type,
  familyDeductibleAmount: networkBenefit.familyDeductible?.amount,
  annualMaximumType: networkBenefit.annualMaximum?.type,
  annualMaximumAmount: networkBenefit.annualMaximum?.amount,
  preventativeType1Type: networkBenefit.preventativeType1?.type,
  preventativeType1Amount: networkBenefit.preventativeType1?.amount,
  basicType2Type: networkBenefit.basicType2?.type,
  basicType2Amount: networkBenefit.basicType2?.amount,
  majorType3Type: networkBenefit.majorType3?.type,
  majorType3Amount: networkBenefit.majorType3?.amount,
  orthoType4Type: networkBenefit.orthoType4?.type,
  orthoType4Amount: networkBenefit.orthoType4?.amount,
  orthoMaximumType: networkBenefit.orthoMaximum?.type,
  orthoMaximumAmount: networkBenefit.orthoMaximum?.amount,
  orthoAgeLimitType: networkBenefit.orthoAgeLimit?.type,
  orthoAgeLimitAge: networkBenefit.orthoAgeLimit?.age,
});

/**
 * Transform from API format to Grid format
 */
export const transformDentalPpoBenefitsFromApi = (
  benefits: RfpDentalBenefit & { extended?: DentalExtendedBenefitApi },
): Partial<DentalPpoBenefitGrid> => {
  const transformed: Partial<DentalPpoBenefitGrid> = {
    ...benefits,
    objectType: DentalObjectTypeEnum.dental,
  };

  // networkName is carried per-network by transformNetworkBenefitFromApi (spread),
  // so each network keeps its own value.
  transformed.networkBenefits =
    benefits.networkBenefits?.map(transformNetworkBenefitFromApi) || [];

  // Extended (non-v5) fields — CD-1659/CD-1660
  Object.assign(
    transformed,
    transformDentalPpoExtendedFromApi(benefits.extended),
  );

  // Keep service waiting periods synced
  if (Array.isArray(benefits.serviceWaitingPeriods)) {
    transformed.serviceWaitingPeriodsMonths = benefits.serviceWaitingPeriods;
  }

  return transformed;
};

type DentalPpoExtendedGridFields = Pick<
  DentalPpoBenefitGrid,
  | "preventiveAdvantage"
  | "oralExams"
  | "oralExamFrequency"
  | "cleaningFrequency"
  | "bitewingXRays"
  | "fullMouthXRays"
  | "fullMouthXRaysFrequency"
  | "fluorideTreatments"
  | "amalgamSilverFillings"
  | "simpleExtractions"
  | "bridgesDentures"
  | "bridgesDenturesFrequency"
  | "crowns"
  | "crownsFrequency"
  | "tmj"
  | "workInProgressIncluded"
  | "openOrClosedContract"
  | "eligibilityHours"
  | "weightedAvgOutOfPocket"
>;

// Maps each grid key to its API key. Grid and API field names diverge for
// three fields (preventiveAdvantage vs preventativeAdvantage,
// bridgesDentures(Frequency) vs bridgesAndDentures(Frequency)) per the
// existing MP placeholder spellings — every other pair is identical.
const DENTAL_PPO_EXTENDED_FIELD_KEYS: [
  keyof DentalPpoExtendedGridFields,
  keyof DentalExtendedBenefitApi,
][] = [
  ["preventiveAdvantage", "preventativeAdvantage"],
  ["oralExams", "oralExams"],
  ["oralExamFrequency", "oralExamFrequency"],
  ["cleaningFrequency", "cleaningFrequency"],
  ["bitewingXRays", "bitewingXRays"],
  ["fullMouthXRays", "fullMouthXRays"],
  ["fullMouthXRaysFrequency", "fullMouthXRaysFrequency"],
  ["fluorideTreatments", "fluorideTreatments"],
  ["amalgamSilverFillings", "amalgamSilverFillings"],
  ["simpleExtractions", "simpleExtractions"],
  ["bridgesDentures", "bridgesAndDentures"],
  ["bridgesDenturesFrequency", "bridgesAndDenturesFrequency"],
  ["crowns", "crowns"],
  ["crownsFrequency", "crownsFrequency"],
  ["tmj", "tmj"],
  ["workInProgressIncluded", "workInProgressIncluded"],
  ["openOrClosedContract", "openOrClosedContract"],
  ["eligibilityHours", "eligibilityHours"],
  ["weightedAvgOutOfPocket", "weightedAvgOutOfPocket"],
];

// Extended (non-v5) fields — CD-1659/CD-1660. Split out of
// transformDentalPpoBenefitsFromApi to keep that function's complexity down.
// Every grid key is present (as undefined) even when `extended` itself is
// absent, so a later edit to any other field doesn't leave a stale extended
// value in the grid row from a previous plan.
const transformDentalPpoExtendedFromApi = (
  extended: DentalExtendedBenefitApi | undefined,
): DentalPpoExtendedGridFields =>
  Object.fromEntries(
    DENTAL_PPO_EXTENDED_FIELD_KEYS.map(([gridKey, apiKey]) => [
      gridKey,
      extended?.[apiKey] ?? undefined,
    ]),
  ) as DentalPpoExtendedGridFields;

/**
 * Grid keys that persist under `benefits.extended` — see
 * STD_EXTENDED_GRID_FIELD_KEYS. Taken from buildDentalPpoExtendedForApi's grid
 * side, which spells preventive/bridgesDentures differently from the API.
 * `weightedAvgOutOfPocket` round-trips but has no Dental PPO grid row, so it
 * is left out of a set that names editable cells.
 */
export const DENTAL_PPO_EXTENDED_GRID_FIELD_KEYS: ReadonlySet<string> = new Set(
  [
    "preventiveAdvantage",
    "oralExams",
    "oralExamFrequency",
    "cleaningFrequency",
    "bitewingXRays",
    "fullMouthXRays",
    "fullMouthXRaysFrequency",
    "fluorideTreatments",
    "amalgamSilverFillings",
    "simpleExtractions",
    "bridgesDentures",
    "bridgesDenturesFrequency",
    "crowns",
    "crownsFrequency",
    "tmj",
    "workInProgressIncluded",
    "openOrClosedContract",
    "eligibilityHours",
  ],
);
